import React from "react";
import { Navigate, Outlet, useParams } from "react-router-dom";
import { useAuth } from "../providers/AuthProvider";

/**
 * RequireRole — route guard that replaces the old privateRouter.js.
 *
 * Old privateRouter only checked localStorage for any token.
 * RequireRole checks:
 *   1. isAuthenticated from AuthProvider (real session, not just a stale localStorage value).
 *   2. Optionally: whether the user's role is in the allowed `roles` list.
 *
 * @param {string[]} [roles]  - Allowed roles. If omitted, any authenticated user passes.
 *
 * Redirect behaviour:
 *   - Not authenticated  → /login
 *   - Wrong role         → /unauthorized
 *   - Loading            → nothing rendered (avoids flash-of-redirect)
 *
 * Usage in a feature's routes.js:
 *   { path: '/:slug/app/dashboard', element: <RequireRole roles={['OWNER']}><Dashboard /></RequireRole> }
 *   OR as a layout route:
 *   { path: '/:slug/app', element: <RequireRole roles={['TEACHER', 'OWNER']} />, children: [...] }
 */
function RequireRole({ roles: allowed, children }) {
  // Autoriza por TODOS los roles del usuario (rol dual), no por la vista activa:
  // un dueño-docente puede entrar a rutas de dueño y de docente sin importar en
  // qué "modo" esté el switcher. El backend sigue siendo la autoridad.
  const { isAuthenticated, roles: userRoles = [], role, isLoading } = useAuth();
  const { slug } = useParams();

  // While AuthProvider is hydrating the session, render nothing to avoid a
  // premature redirect that would log out a user whose token is still loading.
  if (isLoading) return null;

  if (!isAuthenticated) {
    // Stay within the tenant: send slug-scoped routes to /{slug}/login, and
    // legacy (slug-less) routes to the legacy /login.
    return <Navigate to={slug ? `/${slug}/login` : "/login"} replace />;
  }

  const held = userRoles.length ? userRoles : role ? [role] : [];
  if (allowed && allowed.length > 0 && !allowed.some((r) => held.includes(r))) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Supports both wrapping children and acting as a layout route (with <Outlet />)
  return children ? <>{children}</> : <Outlet />;
}

export { RequireRole };
